import * as React from 'react'
import produce from 'immer'
import moment from 'moment'
import { Form, Input, Tabs, Spin, Dropdown, Menu, Button, Tooltip, message } from 'antd'
import { isNil, get, set, range, isEmpty, cloneDeep, invoke, round, isEqual, flow, uniq } from 'lodash'
import { saveAs } from 'file-saver'
import { getUserPermission } from 'helpers/auth'
import { setStorageItem } from 'helpers/localStorage'
import { getKeyValueItem, setKeyValueItem } from 'helpers/keyValueStorage'
import { showError, resourceNotFound, ValidationError, showClientNotifications } from 'helpers/errors'
import { isValidDate, tryParseMoment, formatUserTime, MIN_TIME, MAX_TIME } from 'helpers/dateTime'
import {
  createLabelFactory,
  createFieldFactory,
  createDropdownRender,
  createSaveDocumentItems,
  createSaveToleranceHistoryItems,
  createSaveWarrantyItems,
  createHandleSubmit,
  validateBarcode,
  isReadOnly,
  confirmUnsavedChanges,
} from 'helpers/formViews'
import { setSessionItem } from 'helpers/sessionStorage'
import { HARD_SPACE } from 'helpers/utils'
import { stopEvent, Emitter } from 'helpers/events'
import linkTargets from 'options/linkTargets'
import {
  createSearchAssets,
  createSearchOperators,
  createSearchAssignedOperators,
  createSaveChecklistItems,
  createSaveLabourItems,
  prioritizeChecklistItems,
} from 'helpers/jobs'
import { withRouter } from 'react-router-dom'
import { createPrintHandler, resetPrintHandler } from 'helpers/print'
import { t, toLocaleNumber } from 'helpers/i18n'
import { getMimeType, getFileExtension } from 'helpers/downloadItems'
import { createSaveFormInstances, createRefreshFormInstances, FormInstancesContext } from 'helpers/formInstances'
import { TRACK_ASSETS_AFTER_CLOSE_KEY } from 'components/TrackAssets'
import { FORM_INSTANCES_REFRESH, FORM_CHANGE_EVENT } from 'options/events'
import Print from 'containers/Jobs/Print'
import FormInstances from 'containers/Jobs/Forms'
import SelectJobTemplates from 'containers/JobTemplates/Select'
import Checklist from 'containers/Jobs/Checklist'
import Tolerances from 'containers/Assets/Tolerances'
import Labour from 'containers/Jobs/Labour'
import Warranties from 'containers/Assets/Warranties'
import Documents from 'containers/Documents/ChildListView'
import Lists from 'containers/Lists/ChildListView'
import Comments from 'elements/Comments'
import Select, { Option, getOptionProps } from 'elements/Select'
import Icon from 'elements/Icon'
import DatePicker from 'elements/DatePicker'
import InputNumber from 'elements/InputNumber'
import CheckboxList from 'elements/CheckboxList'
import Checkbox from 'elements/Checkbox'
import Modal from 'elements/Modal'
import { Row, Col } from 'elements/Grid'
import Help from 'elements/Help'
import Drawer from 'elements/Drawer'
import FormButtons from 'elements/FormButtons'
import SelectOperators from 'containers/Operators/Select'
import SendEmail from 'components/SendEmail'

const JobTasksFromView = linkTargets.jobTaskRecord.formComponent
const AssetsFormView = linkTargets.assetRecord.formComponent

const getDowntime = ({ downtimeStart, downtimeEnd }) =>
  isValidDate(downtimeStart) && isValidDate(downtimeEnd)
    ? round(moment.duration(moment(downtimeEnd).diff(moment(downtimeStart))).asHours(), 2)
    : 0

class FormView extends React.Component {
  state = {
    checklistItems: [],
    labourItems: [],
    listItems: [],
    warrantyItems: [],
    toleranceItems: [],
  }

  constructor(props) {
    super(props)

    this.printableRef = React.createRef()
    this.handleSubmit = createHandleSubmit(this)
    this.searchAssets = createSearchAssets(this)
    this.searchOperators = createSearchOperators(this)
    this.searchAssignedToOperators = createSearchAssignedOperators(this)
    this.saveDocumentItems = createSaveDocumentItems(this, 'Job')
    this.saveToleranceHistoryItems = createSaveToleranceHistoryItems(this)
    this.saveWarrantyItems = createSaveWarrantyItems(this)
    this.saveChecklistItems = createSaveChecklistItems(this)
    this.saveLabourItems = createSaveLabourItems(this)
    this.saveFormInstances = createSaveFormInstances(this, { objectType: 'Job' })
    this.refreshFormInstances = createRefreshFormInstances(this, { objectType: 'Job' })
  }

  componentDidMount() {
    this.fetchItem()

    Emitter.on(FORM_INSTANCES_REFRESH, this.refreshFormInstances)
    Emitter.on(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  componentWillUnmount() {
    Emitter.off(FORM_INSTANCES_REFRESH, this.refreshFormInstances)
    Emitter.off(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  promiseState = (state = {}) => new Promise((resolve) => this.setState(state, resolve))

  isTemplate = () =>
    Boolean(this.props.isTemplate) ||
    Boolean(this.props.linkTargetRecord?.legacyReportTaskSettings?.templateId) ||
    Boolean(this.props.linkTargetRecord?.jobTaskSettings?.templateId)

  handleFormChange = () => this.setState({ formHasChanged: true })

  getItemId = () =>
    [
      this.state.item?.id,
      this.props.linkTargetRecord?.legacyReportTaskSettings?.templateId,
      this.props.linkTargetRecord?.jobTaskSettings?.templateId,
      this.props.linkTargetRecord?.jobId,
      this.props.linkTargetRecord?.objectId,
    ].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const { isScheduledJob } = this.props.linkTargetRecord ?? {}
      const responses = await Promise.all([
        isScheduledJob
          ? Promise.resolve({ value: { data: this.props.linkTargetRecord } })
          : itemId
            ? this.props.getItem(itemId).catch((error) => {
                this.props.onCancel?.()
                throw error
              })
            : !this.props.linkTargetRecord
              ? this.props.newItem({})
              : resourceNotFound(this),
        this.props.getSettings({ type: this.isTemplate() ? 'jobTemplates' : 'job' }),
        this.props.getFileTemplates({ objectType: 'JobChecklistItem' }),
        this.props.getLocations(),
        this.props.getAssetCategories(),
        this.props.getJobReasons(),
        this.props.getJobGroups({ includeUnassignedSelection: false }),
        this.props.getJobStatusOptions(),
        this.props.getUsers(),
      ])

      const item = cloneDeep(responses[0].value.data)

      if (!item.id) {
        item.assetId = this.props.assetId || item.assetId || 0
        item.locationId = this.props.locationId || item.locationId || 0
        item.assetCategoryId = this.props.assetCategoryId || item.assetCategoryId || 0
      }

      item.locationId = item.locationId || undefined
      item.locationName = item.locationId ? item.locationName : ''

      const [
        assets,
        assignedToOperators,
        operators,
        checklistItems,
        labourItems,
        toleranceItems,
        warrantyItems,
        documentItems,
        formInstanceItems,
      ] = await Promise.all([
        this.props.getAssets({
          assetIds: item.assetId ? [item.assetId] : undefined,
          assetCategoryIds: item.assetCategoryId ? [item.assetCategoryId] : undefined,
          locationIds: item.locationId ? [item.locationId] : undefined,
        }),
        this.props.getOperators({ operatorIds: item.assignedToId ? [item.assignedToId] : undefined }),
        this.props.getOperators({ operatorIds: item.operatorIds }),
        item.id && !item.isScheduledJob ? this.props.getChecklistItems(item.id) : Promise.resolve(null),
        item.id && !item.isScheduledJob ? this.props.getLabourItems(item.id) : Promise.resolve(null),
        item.assetId ? this.props.getToleranceItems(item.assetId) : Promise.resolve(null),
        item.assetId ? this.props.getWarrantyItems(item.assetId) : Promise.resolve(null),
        item.id && !item.isScheduledJob
          ? this.props.getDocumentItems({
              request: {
                domainObjectId: item.id,
                domainObjectType: 'Job',
                documentType: 'ObjectDocument',
              },
            })
          : Promise.resolve(null),
        item.id && !item.isScheduledJob
          ? this.props.getFormInstanceItems({ objectType: 'Job', objectId: item.id })
          : Promise.resolve(null),
      ])

      this.setState({
        item,
        itemOriginal: cloneDeep(item),
        fieldSettings: responses[1].value.data.fieldSettings,
        fileTemplates: get(responses[2], 'value.data.items', []),
        locations: get(responses[3], 'value.data.items', []),
        assetCategories: get(responses[4], 'value.data.items', []),
        jobReasons: get(responses[5], 'value.data.items', []),
        jobGroups: get(responses[6], 'value.data.items', []),
        jobStatusOptions: get(responses[7], 'value.data.items', []),
        users: get(responses[8], 'value.data.items', []),
        assets: assets?.value?.data ?? {},
        assignedToOperators: assignedToOperators?.value?.data ?? {},
        operators: operators?.value?.data ?? {},
        checklistItems: get(checklistItems, 'value.data.items', []),
        checklistItemsOriginal: get(checklistItems, 'value.data.items', []),
        checklistItemsTotals: get(checklistItems, 'value.data.pageTotals', {}),
        labourItems: get(labourItems, 'value.data.items', []),
        labourItemsOriginal: get(labourItems, 'value.data.items', []),
        labourItemsTotals: get(labourItems, 'value.data.pageTotals', {}),
        toleranceItems: get(toleranceItems, 'value.data.items', []),
        toleranceItemsOriginal: get(toleranceItems, 'value.data.items', []),
        toleranceItemsTotals: get(toleranceItems, 'value.data.pageTotals', {}),
        warrantyItems: get(warrantyItems, 'value.data.items', []),
        warrantyItemsOriginal: get(warrantyItems, 'value.data.items', []),
        warrantyItemsTotals: get(warrantyItems, 'value.data.pageTotals', {}),
        documentItems: get(documentItems, 'value.data.items', []),
        documentItemsOriginal: get(documentItems, 'value.data.items', []),
        documentItemsTotals: get(documentItems, 'value.data.pageTotals', {}),
        formInstanceItems: get(formInstanceItems, 'value.data.items', []),
        formInstanceItemsOriginal: get(formInstanceItems, 'value.data.items', []),
        formInstanceItemsTotals: get(formInstanceItems, 'value.data.pageTotals', {}),
        formHasChanged: false,
      })
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) => {
    if (!this.state.item.userCanEditRecord) {
      return
    }

    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        if (name === 'locationId') {
          draft.item.assetId = 0
          draft.tabsActiveKey = 'general'
        }

        if (name === 'assetCategoryId') {
          draft.item.assetId = 0
          draft.tabsActiveKey = 'general'
        }

        if (name === 'assetId' && draft.assets?.items) {
          if (value) {
            const locationId = draft.assets.items.find((one) => one.id === value).locationId

            if (locationId && draft.locations.find((one) => one.id === locationId)) {
              draft.item.locationId = locationId
            } else {
              draft.item.locationId = null
            }
          } else {
            draft.tabsActiveKey = 'general'
          }
        }

        if (name === 'approvedToBegin') {
          if (value) {
            draft.item.approvedToBeginBy = this.props.user.userName
            draft.item.approvedToBeginDate = new Date()
          } else {
            draft.item.approvedToBeginBy = null
            draft.item.approvedToBeginDate = null
            draft.item.approvedToClose = false
            draft.item.approvedToCloseBy = null
            draft.item.approvedToCloseDate = null
            draft.item.active = true
            draft.item.completedBy = null
            draft.item.completionDate = null
          }
        }

        if (name === 'approvedToClose') {
          if (value) {
            draft.item.approvedToCloseBy = this.props.user.userName
            draft.item.approvedToCloseDate = new Date()
            draft.item.approvedToBegin = true
            draft.item.approvedToBeginBy = draft.item.approvedToBeginBy || this.props.user.userName
            draft.item.approvedToBeginDate = draft.item.approvedToBeginDate || new Date()
          } else {
            draft.item.approvedToCloseBy = null
            draft.item.approvedToCloseDate = null
            draft.item.active = true
            draft.item.completedBy = null
            draft.item.completionDate = null
          }
        }

        if (name === 'active') {
          if (value) {
            draft.item.completedBy = null
            draft.item.completionDate = null
            draft.item.downtimeEnd = null
            draft.item.productionDowntime = 0
          } else {
            draft.item.completedBy = this.props.user.userName
            draft.item.completionDate = new Date()
            draft.item.downtimeEnd = new Date()
            draft.item.approvedToBegin = true
            draft.item.approvedToBeginBy = draft.item.approvedToBeginBy || this.props.user.userName
            draft.item.approvedToBeginDate = draft.item.approvedToBeginDate || new Date()
            draft.item.approvedToClose = true
            draft.item.approvedToCloseBy = draft.item.approvedToCloseBy || this.props.user.userName
            draft.item.approvedToCloseDate = draft.item.approvedToCloseDate || new Date()
          }
        }

        if (name === 'reasonId') {
          const { duration = 0, useDuration = false } = draft.jobReasons.find((one) => one.id === value) || {}

          if (useDuration) {
            if (this.isTemplate()) {
              draft.item.duration = duration
            } else {
              draft.item.dueDate = new Date(new Date().getTime() + duration * 24 * 60 * 60 * 1000)
            }
          }
        }

        if (name === 'trackDowntime') {
          if (value === false) {
            draft.item.productionEqualsElapsed = true
            draft.item.productionDowntime = getDowntime(draft.item)
          }
        }

        if (!draft.item.active && draft.item.productionEqualsElapsed) {
          draft.item.productionDowntime = getDowntime(draft.item)
        }

        this.props.form.setFieldsValue({
          locationId: draft.item.locationId || null,
          assetId: draft.item.assetId || 0,
          assetCategoryId: draft.item.assetCategoryId || 0,
          productionDowntime: draft.item.productionDowntime || 0,
          downtimeStart: draft.item.downtimeStart,
          downtimeEnd: draft.item.downtimeEnd,
          priority: draft.item.priority || 0,
          assignedToId: draft.item.assignedToId || 0,
        })
      }),
      async () => {
        if (['locationId', 'assetCategoryId'].includes(name)) {
          await this.props.onSave?.(true)

          try {
            const { assetId, assetCategoryId, locationId } = this.state.item

            const assets = await this.props
              .getAssets({
                assetIds: assetId ? [assetId] : undefined,
                assetCategoryIds: assetCategoryId ? [assetCategoryId] : undefined,
                locationIds: locationId ? [locationId] : undefined,
              })
              .then((r) => r.value.data)

            this.setState({
              assets,
              toleranceItems: [],
              toleranceItemsOriginal: [],
              warrantyItems: [],
              warrantyItemsOriginal: [],
            })
          } catch (error) {
            showError({ error })
          }

          await this.props.onSave?.(false)
        }

        if (name === 'assetId') {
          if (value > 0) {
            try {
              const [toleranceItems, warrantyItems] = await Promise.all([
                this.props.getToleranceItems(value),
                this.props.getWarrantyItems(value),
              ])

              this.setState({
                toleranceItems: get(toleranceItems, 'value.data.items', []),
                toleranceItemsOriginal: get(toleranceItems, 'value.data.items', []),
                toleranceItemsTotals: get(toleranceItems, 'value.data.pageTotals', {}),
                warrantyItems: get(warrantyItems, 'value.data.items', []),
                warrantyItemsOriginal: get(warrantyItems, 'value.data.items', []),
                warrantyItemsTotals: get(warrantyItems, 'value.data.pageTotals', {}),
              })
            } catch (error) {
              showError({ error })
            }
          } else {
            this.setState({
              toleranceItems: [],
              toleranceItemsOriginal: [],
              warrantyItems: [],
              warrantyItemsOriginal: [],
            })
          }
        }
      }
    )
  }

  hasUnsavedChanges = () =>
    this.state.formHasChanged ||
    !this.state.item?.id ||
    !isEqual(this.state.item, this.state.itemOriginal) ||
    !isEqual(this.state.checklistItems, this.state.checklistItemsOriginal) ||
    !isEqual(this.state.labourItems, this.state.labourItemsOriginal) ||
    !isEqual(this.state.warrantyItems, this.state.warrantyItemsOriginal) ||
    !isEqual(this.state.toleranceItems, this.state.toleranceItemsOriginal) ||
    !isEqual(this.state.documentItems, this.state.documentItemsOriginal)

  saveItem = async (item) => {
    if (!item) {
      throw new Error('item is undefined')
    }

    const saved = item.id ? await this.props.updateItem(item) : await this.props.createItem(item)

    await Promise.all([
      this.saveChecklistItems(saved.value.data.id),
      this.saveLabourItems(saved.value.data.id),
      this.saveWarrantyItems(saved.value.data.assetId),
      this.saveToleranceHistoryItems(saved.value.data.assetId),
      this.saveDocumentItems(saved.value.data.id),
    ])

    return saved
  }

  transformSaveItemParams = ({ item, values }) => ({
    downtimeStart: this.isTemplate() ? item.createdDate : item.downtimeStart,
  })

  handleActionsMenuClick = async ({ key }) => {
    switch (key) {
      case get(key.match(/^fileTemplate:\d+$/), 'input'):
        try {
          const fileTemplateId = key.replace(/^fileTemplate:/, '')
          const fileTemplate = await this.props.getFileTemplate(fileTemplateId).then((r) => r.value.data)
          const acceptMimeType = getMimeType(fileTemplate.fileTemplateFileType)

          const response = await this.props.downloadChecklistItems(this.state.item.id, {
            acceptMimeType,
            selectedColumnKeys: fileTemplate.fields,
          })

          saveAs(response.value.data, `${fileTemplate.name}.${getFileExtension(acceptMimeType)}`)
        } catch (error) {
          showError({ error })
        }
        break

      case 'createFromTemplate':
        this.setState({ createFromTemplateVisible: true })
        break

      case 'uploadChecklist':
        confirmUnsavedChanges(() => {
          this.props.history.push('/tools/dataExchange/uploadData?objectType=JobChecklistItem')
          this.props.onCancel()
        })
        break

      case 'addToSchedule':
        this.setState({
          addToScheduleVisible: true,
          addToScheduleSaving: false,
        })
        break

      case 'sendEmail':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          this.setState({ sendEmailVisible: true })
        }
        break

      case 'print':
        this.setState(
          produce((draft) => {
            draft.printItem = { ...draft.item, ...this.props.form.getFieldsValue() }
            draft.printVisible = true
          })
        )
        break

      case 'issueItems':
        confirmUnsavedChanges(async () => {
          try {
            setStorageItem('inventory.issue.formView', {
              quickscan: true,
              requireQuantity: true,
            })
            await getKeyValueItem('inventory.issue.sideForm')
            await setKeyValueItem('inventory.issue.sideForm', {
              locationId: this.state.item.locationId,
              jobId: this.state.item.id,
              jobName: this.state.item.name,
              jobNumber: this.state.item.number,
              jobBarcode: this.state.item.barcode,
              jobDisplayName: `${this.state.item.barcode} - ${this.state.item.name}`,
              operatorId: this.props.user.operatorId,
              operatorBarcode: this.props.user.operatorBarcode,
              operatorDisplayName: this.props.user.operatorName,
              assetId: this.state.item.assetId && this.state.item.assetName ? this.state.item.assetId : undefined,
              assetDisplayName:
                this.state.item.assetId && this.state.item.assetName ? this.state.item.assetName : undefined,
            })
            this.props.history.push('/inventory/issue')
            this.props.onCancel()
          } catch (error) {
            showError({ error })
          }
        })
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  handleCreateFromTemplateOk = async () => {
    try {
      await this.props.onSave?.(true)

      this.setState({ createFromTemplateLoading: true })

      const saved = await this.props.createFromTemplate(this.state.createFromTemplateRecord.id, {
        assetId: this.state.item.assetId ?? 0,
        assetLocationId: this.state.item.locationId ?? 0,
        assignedOperatorId: this.state.item.assignedToId ?? 0,
      })

      await this.promiseState({
        item: null,
        createFromTemplateVisible: false,
        createFromTemplateRecord: null,
      })
      await this.fetchItem({ itemId: saved.value.data.id })
      await this.props.onSave?.(false, saved.value.data)
    } catch (error) {
      showError({ error })
      await this.props.onSave?.(false)
    } finally {
      this.setState({ createFromTemplateLoading: false })
    }
  }

  requestApproval = async (action) => {
    try {
      this.setState({ requestApprovalLoading: true })

      await invoke(this.props, action, this.state.item.id)

      message.success(t('requestSentSuccessfully'))
    } catch (error) {
      showError({ error })
    } finally {
      this.setState({ requestApprovalLoading: false })
    }
  }

  handleChangeCloseDateOk = () =>
    this.setState({ changeCloseDateVisible: false }, () =>
      this.setItemValue('completionDate', this.state.completionDateToUpdate)
    )

  handleGenerateClick = async (e) => {
    let saved = null

    try {
      stopEvent(e)

      const params = {
        ...this.props.form.getFieldsValue(),
        isTemplate: this.isTemplate(),
        downtimeStart: new Date().toJSON(),
        createdDate: new Date().toJSON(),
        createdBy: this.props.user.userName,
        id: undefined,
      }

      if (!params.locationId) {
        message.error(t('selectLocationFirst'))
        return
      }

      this.setState({ validationErrors: null, generateButtonLoading: true })

      await this.props.onSave?.(true)

      saved = await this.props.generateId(params).then((r) => r.value.data)

      await this.fetchItem({ itemId: saved.id })

      await this.props.onSave?.(false, saved)
    } catch (error) {
      showError({ error })
      await this.props.onSave?.(false, saved)
    } finally {
      this.setState({ generateButtonLoading: false })
    }
  }

  handleSendEmailSubmit = async (recipients) => {
    try {
      this.setState({ sendEmailLoading: true })

      const response = await this.props.emailJob(this.state.item.id, recipients)

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }

      this.setState({
        sendEmailLoading: false,
        sendEmailVisible: false,
      })
    } catch (error) {
      showError({ error })
      this.setState({ sendEmailLoading: false })
    }
  }

  render() {
    const { item, fieldSettings, operators, assignedToOperators, assets, checklistItems, toleranceItems, jobGroups } =
      this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(this.state.fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, this.state.fieldSettings)

    // Permissions
    const requireChecklist = this.props.customer.generalSettings.requireChecklistCompleteToCloseJob
    const enableActiveCheckbox =
      item.id &&
      getUserPermission('CloseJobs') === 'Yes' &&
      (!requireChecklist || !checklistItems.find((one) => !one.complete))
    const enableDowntimeStart = item.trackDowntime && getUserPermission('EditJobCloseDate') === 'Yes'
    const enableDowntimeEnd = !item.active && item.trackDowntime && getUserPermission('EditJobCloseDate') === 'Yes'
    const enableProductionDowntime =
      !item.active &&
      item.trackDowntime &&
      !item.productionEqualsElapsed &&
      getUserPermission('EditJobCloseDate') === 'Yes'
    const enableProductionEqualsElapsedCheckbox =
      !item.active && item.trackDowntime && getUserPermission('EditJobCloseDate') === 'Yes'
    const readOnly = isReadOnly(this) || item.userCanEditRecord === false || item.isScheduledJob
    const userCanApproveRecord =
      item.id && (getUserPermission('CloseJobs') === 'Yes' || getUserPermission('Jobs') === 'All')
    const checklistFileTemplates = this.state.fileTemplates ?? []

    return (
      <>
        <Form layout="vertical" onSubmit={readOnly ? stopEvent : this.handleSubmit} colon={false}>
          {this.isTemplate() && this.props.enableAssetManagement && (
            <Dropdown
              overlay={
                <Menu onClick={this.handleActionsMenuClick}>
                  <Menu.Item key="addToSchedule" disabled={!item.id}>
                    {t('addToSchedule')}
                  </Menu.Item>
                </Menu>
              }
              trigger={['click']}
            >
              <Button className="form-items-actions">
                {t('actions')}
                <Icon type="KeyboardArrowDown" />
              </Button>
            </Dropdown>
          )}
          <Dropdown
            overlay={
              item.isScheduledJob || this.isTemplate() ? (
                <Menu onClick={this.handleActionsMenuClick}>
                  {!isEmpty(checklistFileTemplates) && (
                    <Menu.SubMenu key="downloadChecklist" title={t('downloadChecklist')} disabled={!item.id}>
                      {checklistFileTemplates.map((each) => (
                        <Menu.Item key={`fileTemplate:${each.id}`}>{each.name}</Menu.Item>
                      ))}
                    </Menu.SubMenu>
                  )}
                  <Menu.Item key="uploadChecklist" disabled={!item.id || readOnly}>
                    {t('uploadChecklist')}
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item key="print" disabled={!item.id}>
                    {t('print...')}
                  </Menu.Item>
                </Menu>
              ) : (
                <Menu onClick={this.handleActionsMenuClick}>
                  {!item.id
                    ? [
                        <Menu.Item key="createFromTemplate">{t('createFromTemplate')}</Menu.Item>,
                        <Menu.Divider key="createFromTemplateDivider" />,
                      ]
                    : null}
                  {getUserPermission('Issue') === 'Yes' && [
                    <Menu.Item key="issueItems">{t('issueItems')}</Menu.Item>,
                    <Menu.Divider key="issueItemsDivider" />,
                  ]}
                  {!isEmpty(checklistFileTemplates) && (
                    <Menu.SubMenu key="downloadChecklist" title={t('downloadChecklist')} disabled={!item.id}>
                      {checklistFileTemplates.map((each) => (
                        <Menu.Item key={`fileTemplate:${each.id}`}>{each.name}</Menu.Item>
                      ))}
                    </Menu.SubMenu>
                  )}
                  <Menu.Item key="uploadChecklist" disabled={!item.id || readOnly}>
                    {t('uploadChecklist')}
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item key="sendEmail" disabled={!item.id}>
                    {t('email')}
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item key="print" disabled={!item.id}>
                    {t('print...')}
                  </Menu.Item>
                </Menu>
              )
            }
            trigger={['click']}
          >
            <Button className="form-items-actions">
              {t('actions')}
              <Icon type="KeyboardArrowDown" />
            </Button>
          </Dropdown>
          <div className="form-items-container">
            <ValidationError errors={this.state.validationErrors} />
            <Row>
              <Col third>
                <Form.Item label={createLabel('locationId')}>
                  {createFieldDecorator('locationId')(
                    <Select onChange={(value) => this.setItemValue('locationId', value)} readOnly={readOnly}>
                      {(this.state.locations ?? []).map((each) => (
                        <Option key={each.id} value={each.id}>
                          <span {...getOptionProps(each)}>{each.displayName}</span>
                        </Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
                <Form.Item label={createLabel('barcode')}>
                  <Row gutter={8} type="flex">
                    <Col xs={!item.id ? 16 : 24}>
                      {createFieldDecorator('barcode', { extraRules: [validateBarcode()] })(
                        <Input readOnly={readOnly} />
                      )}
                    </Col>
                    {!item.id && (
                      <Col xs={8}>
                        <Button
                          className="w-full"
                          onClick={this.handleGenerateClick}
                          loading={this.state.generateButtonLoading}
                        >
                          {t('generate')}
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Form.Item>
                <Form.Item label={createLabel('number')}>
                  {createFieldDecorator('number')(<Input readOnly={readOnly} />)}
                </Form.Item>
              </Col>
              <Col third>
                <Form.Item label={createLabel('name')}>
                  {createFieldDecorator('name')(<Input readOnly={readOnly} autoFocus />)}
                </Form.Item>
                <Form.Item label={createLabel('created')}>
                  <Input value={formatUserTime(item.createdDate, item.createdBy) || t('na')} disabled />
                </Form.Item>
                {!this.isTemplate() && this.props.enableAssetManagement && (
                  <Form.Item label={createLabel('requestedBy')}>
                    {createFieldDecorator('requestedBy')(<Input disabled />)}
                  </Form.Item>
                )}
              </Col>
              {this.props.enableAssetManagement && (
                <Col third>
                  {!this.isTemplate() && (
                    <>
                      <Form.Item
                        label={HARD_SPACE}
                        help={
                          item.approvedToBegin
                            ? formatUserTime(item.approvedToBeginDate, item.approvedToBeginBy)
                            : undefined
                        }
                      >
                        <Checkbox
                          onChange={(e) => this.setItemValue('approvedToBegin', e.target.checked)}
                          checked={item.approvedToBegin}
                          disabled={!userCanApproveRecord}
                          readOnly={readOnly}
                        >
                          {createLabel('approvedToBegin')}
                        </Checkbox>
                      </Form.Item>
                      <Form.Item
                        label={HARD_SPACE}
                        help={
                          item.approvedToClose
                            ? formatUserTime(item.approvedToCloseDate, item.approvedToCloseBy)
                            : undefined
                        }
                      >
                        <Checkbox
                          onChange={(e) => this.setItemValue('approvedToClose', e.target.checked)}
                          checked={item.approvedToClose}
                          disabled={!userCanApproveRecord}
                          readOnly={readOnly}
                        >
                          {createLabel('approvedToClose')}
                        </Checkbox>
                      </Form.Item>
                      <Form.Item
                        label={HARD_SPACE}
                        help={!item.active ? formatUserTime(item.completionDate, item.completedBy) : undefined}
                      >
                        <Checkbox
                          onChange={(e) => this.setItemValue('active', !e.target.checked)}
                          checked={!item.active}
                          disabled={!enableActiveCheckbox}
                          readOnly={readOnly}
                        >
                          {createLabel('active')}{' '}
                          {item.active && getUserPermission('CloseJobs') !== 'Yes'
                            ? `- ${t('needPermissionToClose')}`
                            : item.active && requireChecklist && !isEmpty(item.checklistItems)
                              ? `- ${t('requiresCompletedChecklist')}`
                              : null}
                        </Checkbox>
                      </Form.Item>
                    </>
                  )}
                </Col>
              )}
            </Row>
            <Row gutter={8}>
              <Col xs={24} sm={!this.isTemplate() && this.props.enableAssetManagement ? 16 : 24}>
                <Form.Item label={createLabel('description')}>
                  {createFieldDecorator('description')(<Input readOnly={readOnly} />)}
                </Form.Item>
              </Col>
              {!this.isTemplate() && this.props.enableAssetManagement && (
                <Col className="whitespace-nowrap" third>
                  <Form.Item label={HARD_SPACE}>
                    {!item.approvedToBegin && !item.approvedToClose && item.active && (
                      <>
                        <Button
                          onClick={() => this.requestApproval('requestApprovalToBegin')}
                          disabled={!item.id || readOnly}
                          loading={this.state.requestApprovalLoading}
                          className="mr-6"
                        >
                          {t('requestApprovalToBegin')}
                        </Button>{' '}
                        <Help title={t('requestApprovalInfo')} />
                      </>
                    )}
                    {item.approvedToBegin && !item.approvedToClose && item.active && (
                      <>
                        <Button
                          onClick={() => this.requestApproval('requestApprovalToClose')}
                          disabled={!item.id || readOnly}
                          loading={this.state.requestApprovalLoading}
                          className="mr-6"
                        >
                          {t('requestApprovalToClose')}
                        </Button>{' '}
                        <Help title={t('requestApprovalInfo')} />
                      </>
                    )}
                    {item.approvedToBegin && item.approvedToClose && !item.active && (
                      <>
                        <Button
                          disabled={!item.id || readOnly || getUserPermission('EditJobCloseDate') !== 'Yes'}
                          onClick={() =>
                            this.setState({
                              changeCloseDateVisible: true,
                              completionDateToUpdate: item.completionDate,
                            })
                          }
                          className="mr-6"
                        >
                          {t('changeCloseDate')}
                        </Button>{' '}
                        <Help title={t('changeCloseDateInfo')} />
                      </>
                    )}
                  </Form.Item>
                </Col>
              )}
            </Row>
            {this.props.enableAssetManagement && (
              <Row>
                <Col third>
                  <Form.Item label={createLabel('assetCategoryId')}>
                    {createFieldDecorator('assetCategoryId')(
                      <Select
                        onChange={(value) => this.setItemValue('assetCategoryId', value || 0)}
                        readOnly={readOnly}
                      >
                        <Option value={0}>{t('all')}</Option>
                        {(this.state.assetCategories ?? []).map((each) => (
                          <Option key={each.id} value={each.id}>
                            <span {...getOptionProps(each)}>{each.displayName}</span>
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
                <Col sm={16} xs={24}>
                  <Form.Item label={createLabel('assetId')}>
                    {['View', 'Edit'].includes(getUserPermission('Assets')) && (
                      <div className="tofino-label-buttons">
                        <Tooltip title={t(getUserPermission('Assets') === 'Edit' ? 'editAsset' : 'viewAsset')}>
                          <Button
                            size="small"
                            disabled={!item.assetId || readOnly}
                            onClick={() =>
                              this.setState({
                                assetDrawerVisible: true,
                                assetDrawerSaving: false,
                              })
                            }
                          >
                            <Icon type="Edit" />
                          </Button>
                        </Tooltip>{' '}
                        <Tooltip title={t('trackAsset')}>
                          <Button
                            size="small"
                            disabled={(!item.assetId && !item.id) || readOnly}
                            onClick={() => {
                              confirmUnsavedChanges(() => {
                                setSessionItem(TRACK_ASSETS_AFTER_CLOSE_KEY, {
                                  redirectUrl: window.location.pathname,
                                  linkTarget: {
                                    linkTargetName: 'jobRecord',
                                    linkTargetRecord: { jobId: this.state.item.id },
                                    linkTargetText: this.state.item.barcode,
                                  },
                                })
                                setStorageItem('assets.trackAssets', {
                                  quickscan: true,
                                  assetId: item.assetId,
                                  jobId: item.id,
                                  operatorId: this.props.user.operatorId,
                                  operatorBarcode: this.props.user.operatorBarcode,
                                  operatorDisplayName: this.props.user.operatorName,
                                })
                                this.props.history.push('/assets/trackAssets')
                                this.props.onCancel()
                              })
                            }}
                          >
                            <Icon type="Directions" />
                          </Button>
                        </Tooltip>
                      </div>
                    )}
                    {createFieldDecorator('assetId')(
                      <Select
                        onChange={(value) => this.setItemValue('assetId', value || 0)}
                        dropdownRender={createDropdownRender(assets)}
                        onSearch={this.searchAssets}
                        readOnly={readOnly}
                        allowClear
                        showSearch
                      >
                        <Option value={0}>{t('none')}</Option>
                        {(assets?.items ?? []).map((each) => (
                          <Option key={each.id} value={each.id}>
                            <span {...getOptionProps(each)}>{each.displayName}</span>
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Tabs
              activeKey={this.state.tabsActiveKey || 'general'}
              onChange={(tabsActiveKey) => this.setState({ tabsActiveKey })}
            >
              {this.props.enableAssetManagement ? (
                <Tabs.TabPane key="general" tab={t('general')} forceRender>
                  <Row>
                    <Col third>
                      <Form.Item label={createLabel('reasonId')}>
                        {createFieldDecorator('reasonId')(
                          <Select
                            onChange={(value) => this.setItemValue('reasonId', value)}
                            allowClear={false}
                            readOnly={readOnly}
                          >
                            <Option value={0}>{t('none')}</Option>
                            {(this.state.jobReasons ?? []).map((each) => (
                              <Option key={each.id} value={each.id}>
                                <span {...getOptionProps(each)}>{each.displayName || each.id}</span>
                              </Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                      <Form.Item label={createLabel('assignedToId')}>
                        {createFieldDecorator('assignedToId')(
                          <Select
                            onChange={(value) => this.setItemValue('assignedToId', value)}
                            dropdownRender={createDropdownRender(assignedToOperators)}
                            onSearch={this.searchAssignedToOperators}
                            readOnly={readOnly}
                            showSearch
                          >
                            <Option value={0}>{t('none')}</Option>
                            {(assignedToOperators?.items ?? []).map((each) => (
                              <Option key={each.id} value={each.id}>
                                <span {...getOptionProps(each)}>{each.displayName}</span>
                              </Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                      <Form.Item label={createLabel('operatorIds')}>
                        <div className="tofino-label-buttons">
                          <Tooltip title={t('selectOperator')}>
                            <Button
                              size="small"
                              disabled={readOnly}
                              onClick={() =>
                                this.setState({
                                  selectOperatorsVisible: true,
                                  selectOperatorsItems: [],
                                  selectOperatorsLoading: false,
                                })
                              }
                            >
                              <Icon type="PersonSearch" />
                            </Button>
                          </Tooltip>
                        </div>
                        <Select
                          value={item.operatorIds}
                          onChange={(value) => this.setItemValue('operatorIds', value)}
                          dropdownRender={createDropdownRender(operators)}
                          onSearch={this.searchOperators}
                          mode="multiple"
                          placeholder={t('none')}
                          readOnly={readOnly}
                          showSearch
                        >
                          {(operators?.items ?? []).map((each) => (
                            <Option key={each.id} value={each.id}>
                              <span {...getOptionProps(each)}>{each.displayName}</span>
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item label={createLabel('priority')}>
                        {createFieldDecorator('priority')(
                          <Select onChange={(value) => this.setItemValue('priority', value)} readOnly={readOnly}>
                            <Option value={0}>{t('none')}</Option>
                            {range(1, 7).map((each) => (
                              <Option key={each} value={each}>
                                {each}
                              </Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                    </Col>
                    <Col third>
                      {this.isTemplate() ? (
                        <Form.Item label={createLabel('duration')}>
                          <InputNumber
                            value={item.duration}
                            onChange={(value) => this.setItemValue('duration', value)}
                            readOnly={readOnly}
                            min={0}
                          />
                        </Form.Item>
                      ) : (
                        <Form.Item label={createLabel('dueDate')}>
                          <DatePicker
                            onChange={(value) => this.setItemValue('dueDate', value)}
                            value={item.dueDate}
                            readOnly={readOnly}
                          />
                        </Form.Item>
                      )}
                      <Form.Item label={createLabel('budgetHours')}>
                        {createFieldDecorator('budgetHours')(<InputNumber readOnly={readOnly} min={0} />)}
                      </Form.Item>
                      {!this.isTemplate() && (
                        <Form.Item label={createLabel('statusOptionId')}>
                          {createFieldDecorator('statusOptionId')(
                            <Select allowClear={false} readOnly={readOnly}>
                              <Option value={0}>{t('none')}</Option>
                              {(this.state.jobStatusOptions ?? []).map((each) => (
                                <Option key={each.id} value={each.id}>
                                  <span {...getOptionProps(each)}>{each.displayName}</span>
                                </Option>
                              ))}
                            </Select>
                          )}
                        </Form.Item>
                      )}
                      <Form.Item label={createLabel('notifyUser')}>
                        {createFieldDecorator('notifyUser')(
                          <Select placeholder={t('none')} readOnly={readOnly} showSearch>
                            <Option value="">{t('none')}</Option>
                            {(this.state.users ?? []).map((each) => (
                              <Option key={each.userName} value={each.userName}>
                                <span {...getOptionProps(each)}>{each.displayName}</span>
                              </Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                    </Col>
                    <Col third>
                      <Form.Item label={HARD_SPACE}>
                        <Checkbox
                          onChange={(e) => this.setItemValue('trackDowntime', e.target.checked)}
                          checked={item.trackDowntime}
                          readOnly={readOnly}
                        >
                          {createLabel('trackDowntime')}
                        </Checkbox>
                      </Form.Item>
                      {!this.isTemplate() && (
                        <>
                          <Form.Item label={createLabel('downtimeStart')}>
                            {createFieldDecorator('downtimeStart', {
                              extraRules: [
                                {
                                  validator: (rule, value, callback) => {
                                    const downtimeStart = tryParseMoment(value, MIN_TIME)
                                    const downtimeEnd = tryParseMoment(item.downtimeEnd, MAX_TIME)
                                    callback(
                                      downtimeStart > downtimeEnd ? [new Error(t('downtimeStartValidationError'))] : []
                                    )
                                  },
                                },
                              ],
                            })(
                              <DatePicker
                                onChange={(value) => this.setItemValue('downtimeStart', value)}
                                placeholder={t('none')}
                                setTimeMode="exact"
                                allowClear={false}
                                disabled={!enableDowntimeStart}
                                readOnly={readOnly}
                                showTime
                              />
                            )}
                          </Form.Item>
                          <Form.Item label={createLabel('downtimeEnd')}>
                            {createFieldDecorator('downtimeEnd', {
                              extraRules: [
                                {
                                  validator: (rule, value, callback) => {
                                    const downtimeStart = tryParseMoment(item.downtimeStart, MIN_TIME)
                                    const downtimeEnd = tryParseMoment(value, MAX_TIME)
                                    callback(
                                      downtimeStart > downtimeEnd ? [new Error(t('downtimeEndValidationError'))] : []
                                    )
                                  },
                                },
                              ],
                            })(
                              <DatePicker
                                onChange={(value) => this.setItemValue('downtimeEnd', value)}
                                placeholder={t('none')}
                                setTimeMode="exact"
                                allowClear={false}
                                disabled={!enableDowntimeEnd}
                                readOnly={readOnly}
                                showTime
                              />
                            )}
                          </Form.Item>
                          <Form.Item label={`${t('elapsedTime')} = ${toLocaleNumber(getDowntime(item))} ${t('hours')}`}>
                            <Row gutter={6}>
                              <Col xs={8}>
                                {createFieldDecorator('productionDowntime')(
                                  <InputNumber
                                    onChange={(value) => this.setItemValue('productionDowntime', value)}
                                    disabled={!enableProductionDowntime}
                                    readOnly={readOnly}
                                  />
                                )}
                              </Col>
                              <Col xs={16}>
                                <Checkbox
                                  onChange={(e) => this.setItemValue('productionEqualsElapsed', e.target.checked)}
                                  checked={item.productionEqualsElapsed}
                                  disabled={!enableProductionEqualsElapsedCheckbox}
                                  readOnly={readOnly}
                                >
                                  {createLabel('productionEqualsElapsed')}
                                </Checkbox>
                              </Col>
                            </Row>
                          </Form.Item>
                        </>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={this.isTemplate() ? 24 : 12} xs={24}>
                      <Form.Item label={createLabel('notes')}>
                        {createFieldDecorator('notes')(<Input.TextArea rows={5} readOnly={readOnly} />)}
                      </Form.Item>
                    </Col>
                    {!this.isTemplate() && (
                      <Col half>
                        <Comments
                          value={item.actionTaken}
                          onChange={(value) => this.setItemValue('actionTaken', value)}
                          rows={5}
                          label={createLabel('actionTaken')}
                          title={t('addActionTaken')}
                          readOnly={readOnly}
                        />
                      </Col>
                    )}
                  </Row>
                </Tabs.TabPane>
              ) : (
                <Tabs.TabPane key="general" tab={t('general')} forceRender>
                  <Row>
                    <Col third>
                      <Form.Item label={createLabel('priority')}>
                        {createFieldDecorator('priority')(
                          <Select
                            value={item.priority || 0}
                            onChange={(value) => this.setItemValue('priority', value || 0)}
                            readOnly={readOnly}
                          >
                            <Option value={0}>{t('none')}</Option>
                            {range(1, 7).map((each) => (
                              <Option key={each} value={each}>
                                {each}
                              </Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                      {!this.isTemplate() && (
                        <Form.Item label={createLabel('statusOptionId')}>
                          {createFieldDecorator('statusOptionId')(
                            <Select allowClear={false} readOnly={readOnly}>
                              <Option value={0}>{t('none')}</Option>
                              {(this.state.jobStatusOptions ?? []).map((each) => (
                                <Option key={each.id} value={each.id}>
                                  <span {...getOptionProps(each)}>{each.displayName}</span>
                                </Option>
                              ))}
                            </Select>
                          )}
                        </Form.Item>
                      )}
                    </Col>
                  </Row>
                </Tabs.TabPane>
              )}
              {this.props.enableAssetManagement && (
                <Tabs.TabPane key="instructions" tab={t('instructions')} forceRender>
                  <Form.Item>
                    {createFieldDecorator('instructions')(
                      <Input.TextArea rows={14} className="w-full" readOnly={readOnly} />
                    )}
                  </Form.Item>
                </Tabs.TabPane>
              )}
              {item.assetId && this.props.enableAssetManagement && (
                <Tabs.TabPane key="loto" tab={t('loto')} forceRender>
                  <Form.Item>
                    <Input.TextArea rows={14} value={item.assetLOTONotes} className="w-full" readOnly />
                  </Form.Item>
                </Tabs.TabPane>
              )}
              {this.props.enableAssetManagement && (
                <Tabs.TabPane key="checklist" tab={t('checklist')} forceRender>
                  {!!item && (
                    <Checklist
                      items={this.state.checklistItems ?? []}
                      pageTotals={this.state.checklistItemsTotals ?? {}}
                      onChange={(values) => this.setState({ checklistItems: prioritizeChecklistItems(values) })}
                      readOnly={readOnly || (requireChecklist && !item.active)}
                      isTemplate={this.isTemplate()}
                      parentRecord={item}
                    />
                  )}
                </Tabs.TabPane>
              )}
              {!this.isTemplate() && this.props.enableAssetManagement && (
                <Tabs.TabPane key="labour" tab={t('labour')} forceRender>
                  <Labour
                    items={this.state.labourItems ?? []}
                    pageTotals={this.state.labourItemsTotals ?? {}}
                    onChange={(values) => this.setState({ labourItems: values })}
                    readOnly={readOnly}
                  />
                </Tabs.TabPane>
              )}
              {this.props.enableAssetManagement &&
                !this.isTemplate() &&
                item?.assetId &&
                (assets?.items ?? []).find((one) => one.id === item.assetId) && [
                  <Tabs.TabPane key="tolerances" tab={t('tolerances')} forceRender>
                    <Tolerances
                      settingsType="jobs"
                      items={this.state.toleranceItems ?? []}
                      pageTotals={this.state.toleranceItemsTotals ?? {}}
                      onChange={(values) => this.setState({ toleranceItems: values })}
                      readOnly={readOnly}
                      parentRecord={item}
                    />
                  </Tabs.TabPane>,
                  <Tabs.TabPane key="warranty" tab={t('warranty')} forceRender>
                    <Warranties
                      items={this.state.warrantyItems ?? []}
                      pageTotals={this.state.warrantyItemsTotals ?? {}}
                      onChange={(values) => this.setState({ warrantyItems: values })}
                      readOnly={readOnly}
                    />
                  </Tabs.TabPane>,
                ]}
              <Tabs.TabPane key="groups" tab={t('groups')} forceRender>
                <Row>
                  <Col third>
                    <Form.Item label={t('memberOf')}>
                      <CheckboxList
                        dataSource={jobGroups}
                        value={item?.jobGroupIds ?? []}
                        onChange={(values) =>
                          this.setItemValue(
                            'jobGroupIds',
                            jobGroups.filter((each) => values.includes(each.id)).map((each) => each.id)
                          )
                        }
                        readOnly={readOnly}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Tabs.TabPane>
              {this.props.enableAssetManagement && (
                <Tabs.TabPane key="documents" tab={t('documents')} forceRender>
                  <Documents
                    domainObjectId={item.id}
                    domainObjectType="Job"
                    items={this.state.documentItems ?? []}
                    pageTotals={this.state.documentItemsTotals ?? {}}
                    onChange={(values) => this.setState({ documentItems: values })}
                    readOnly={readOnly}
                  />
                </Tabs.TabPane>
              )}
              {!isNil(item.id) && (
                <Tabs.TabPane key="lists" tab={t('lists')} forceRender>
                  <Lists
                    entityType="Job"
                    entityId={item.id}
                    domainObjectType="Job"
                    locationId={item.locationId}
                    readOnly={readOnly}
                  />
                </Tabs.TabPane>
              )}
              <Tabs.TabPane key="custom" tab={t('custom')} forceRender>
                <Row>
                  <Col third>
                    {['custom1', 'custom2', 'custom3'].map((each) => (
                      <Form.Item key={each} label={createLabel(each)}>
                        {createFieldDecorator(each)(<Input readOnly={readOnly} />)}
                      </Form.Item>
                    ))}
                  </Col>
                  <Col third>
                    {['custom4', 'custom5'].map((each) => (
                      <Form.Item key={each} label={createLabel(each)}>
                        {createFieldDecorator(each)(<Input readOnly={readOnly} />)}
                      </Form.Item>
                    ))}
                  </Col>
                </Row>
              </Tabs.TabPane>
              {!isNil(item.id) && (getUserPermission('Master') === 'Yes' || getUserPermission('Forms') !== 'None') && (
                <Tabs.TabPane key="forms" tab={t('forms')} forceRender>
                  <FormInstancesContext.Provider value={{}}>
                    <FormInstances
                      settingsType="job"
                      objectType="Job"
                      objectId={item.id}
                      parentRecord={item}
                      items={this.state.formInstanceItems ?? []}
                      pageTotals={this.state.formInstanceItemsTotals ?? {}}
                      onChange={this.saveFormInstances}
                      readOnly={readOnly}
                    />
                  </FormInstancesContext.Provider>
                </Tabs.TabPane>
              )}
            </Tabs>
          </div>
          <div className="form-buttons-container">
            <FormButtons
              readOnly={readOnly}
              onCancel={this.props.onCancel}
              onSubmit={this.handleSubmit}
              saveButtonLoading={this.state.saveButtonLoading}
              saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
            />
          </div>
        </Form>
        <Modal
          title={t('selectTemplate')}
          visible={this.state.createFromTemplateVisible}
          okText={t('select')}
          onOk={this.handleCreateFromTemplateOk}
          okButtonProps={{
            loading: this.state.createFromTemplateLoading,
            disabled: isNil(this.state.createFromTemplateRecord),
          }}
          onCancel={() =>
            this.setState({
              createFromTemplateVisible: false,
              createFromTemplateRecord: null,
            })
          }
          width={992}
        >
          <SelectJobTemplates onSelect={(values) => this.setState({ createFromTemplateRecord: values[0] })} />
        </Modal>
        <Modal
          title={t('changeCloseDate')}
          visible={this.state.changeCloseDateVisible}
          okText={t('update')}
          onOk={this.handleChangeCloseDateOk}
          onCancel={() =>
            this.setState({
              changeCloseDateVisible: false,
              completionDateToUpdate: item.completionDate,
            })
          }
        >
          <p dangerouslySetInnerHTML={{ __html: t('changeCloseDateInfo') }} />
          <Form.Item>
            <DatePicker
              placeholder={t('none')}
              setTimeMode="exact"
              allowClear={false}
              value={this.state.completionDateToUpdate}
              onChange={(value) => this.setState({ completionDateToUpdate: value })}
              showTime
            />
          </Form.Item>
        </Modal>
        {this.isTemplate() && item.id && (
          <Drawer
            title={t('addToSchedule')}
            size={linkTargets.jobTaskRecord.formSize}
            visible={this.state.addToScheduleVisible}
            onClose={() => this.setState({ addToScheduleVisible: false })}
            saving={this.state.addToScheduleSaving}
          >
            <JobTasksFromView
              jobTaskSettings={{
                jobTemplateName: item.name,
                templateId: item.id,
                assetId: item.assetId,
              }}
              onSave={(pending) => this.setState({ addToScheduleSaving: pending })}
              onSaveAndClose={() => this.setState({ addToScheduleVisible: false })}
              onCancel={() => this.setState({ addToScheduleVisible: false })}
            />
          </Drawer>
        )}
        <Modal
          title={t('printPreview')}
          okText={t('print')}
          onOk={createPrintHandler(this.printableRef)}
          onCancel={() => this.setState({ printVisible: false }, resetPrintHandler)}
          width={992}
          visible={this.state.printVisible}
        >
          <div ref={this.printableRef} className="tofino-print-preview">
            <Print
              parentRecord={{
                ...this.state.printItem,
                checklistItems,
                toleranceItems,
              }}
              isTemplate={this.isTemplate()}
            />
          </div>
        </Modal>
        {Boolean(item.assetId) && (
          <Drawer
            title={t(getUserPermission('Assets') === 'Edit' ? 'editAsset' : 'viewAsset')}
            size={linkTargets.assetRecord.formSize}
            visible={this.state.assetDrawerVisible}
            onClose={() => this.setState({ assetDrawerVisible: false })}
            saving={this.state.assetDrawerSaving}
          >
            <AssetsFormView
              linkTargetRecord={item}
              onSave={(pending) =>
                this.promiseState({ assetDrawerSaving: pending }).then(async () => {
                  await this.props.onSave?.(pending)

                  if (!pending) {
                    await this.fetchItem()
                  }
                })
              }
              onSaveAndClose={() => this.setState({ assetDrawerVisible: false })}
              onCancel={() => this.setState({ assetDrawerVisible: false })}
              readOnly={getUserPermission('Assets') !== 'Edit'}
            />
          </Drawer>
        )}
        <Modal
          title={t('selectOperator')}
          visible={this.state.selectOperatorsVisible}
          onCancel={() => this.setState({ selectOperatorsVisible: false })}
          footer={
            <>
              <Button onClick={() => this.setState({ selectOperatorsVisible: false })}>{t('close')}</Button>
              <Button
                onClick={async () => {
                  try {
                    this.setState({ selectOperatorsLoading: true })

                    const operatorIds = uniq([
                      ...item.operatorIds,
                      ...get(this.state, 'selectOperatorsItems', []).map((each) => each.id),
                    ])
                    const response = await this.props.getOperators({ operatorIds })

                    this.setState(
                      produce((draft) => {
                        draft.item.operatorIds = operatorIds
                        draft.operators = get(response, 'value.data')
                        draft.selectOperatorsItems = []
                        draft.selectOperatorsVisible = false
                      })
                    )
                  } catch (error) {
                    showError({ error })
                  } finally {
                    this.setState({ selectOperatorsLoading: false })
                  }
                }}
                loading={this.state.selectOperatorsLoading}
                disabled={isEmpty(this.state.selectOperatorsItems)}
                type="primary"
              >
                {t('select')}
              </Button>
            </>
          }
          width={992}
        >
          <SelectOperators onSelect={(values) => this.setState({ selectOperatorsItems: values })} mode="multiple" />
        </Modal>
        <SendEmail
          visible={this.state.sendEmailVisible}
          loading={this.state.sendEmailLoading}
          onSubmit={this.handleSendEmailSubmit}
          onCancel={() => this.setState({ sendEmailVisible: false })}
        />
      </>
    )
  }
}

export default flow(
  withRouter,
  Form.create({
    onValuesChange: (...params) => Emitter.emit(FORM_CHANGE_EVENT, ...params),
  })
)(FormView)
